<script setup>
import GuestLayout from '@/layouts/GuestLayout.vue'
import { trackMatomoGoal } from '@/plugins/matomo'
import { trackMetaRegistration } from '@/plugins/meta-pixel'
import { login, register } from '@/routes'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

defineOptions({
  layout: (h, page) => h(GuestLayout, { contentWidthClass: 'max-w-3xl' }, () => page),
})

const { t, locale } = useI18n()

const page = usePage()

const form = useForm({
  first_name: page.props.auth.invitation ? page.props.auth.invitation.first_name : '',
  last_name: page.props.auth.invitation ? page.props.auth.invitation.last_name : '',
  email: page.props.auth.invitation ? page.props.auth.invitation.email : (page.props.auth.companyInvitationEmail ?? ''),
  password: '',
  property_count: null,
  accept_legal: false,
  fb_event_id: '',
})

const toast = useToast()

const benefits = ['portfolio', 'financing', 'matching']

const propertyCountOptions = computed(() => [
  { label: t('form.property_count.options.none'), value: '0' },
  { label: t('form.property_count.options.one_to_two'), value: '1-2' },
  { label: t('form.property_count.options.three_to_nine'), value: '3-9' },
  { label: t('form.property_count.options.ten_to_twenty_nine'), value: '10-29' },
  { label: t('form.property_count.options.thirty_plus'), value: '30+' },
])

watch(
  () => form.property_count,
  (propertyCount) => {
    if (propertyCount && form.errors.property_count) {
      form.clearErrors('property_count')
    }
  },
)

watch(
  () => form.accept_legal,
  (accepted) => {
    if (accepted && form.errors.accept_legal) {
      form.clearErrors('accept_legal')
    }
  },
)

const onFormSubmit = () => {
  const fbEventId = crypto.randomUUID()

  form
    .transform((data) => ({
      ...data,
      language: locale.value,
      fb_event_id: fbEventId,
    }))
    .post(register().url, {
      preserveScroll: true,
      onSuccess: () => {
        trackMatomoGoal(2)
        trackMetaRegistration(fbEventId)
      },
      onError: (errors) => {
        if (Object.keys(errors).length === 0) {
          toast.add({
            severity: 'error',
            summary: t('error.summary'),
            detail: t('error.detail'),
            life: 3000,
          })
        }
        form.reset('password')
      },
    })
}
</script>

<template>
  <Head :title="t('title')" />

  <AuthCard gap="0rem">
    <template #title>
      <span class="text-success-800 mb-2 block text-sm font-semibold tracking-wide uppercase">
        {{ t('eyebrow') }}
      </span>
      <span class="block text-3xl font-black">{{ t('title') }}</span>
    </template>
    <template #subtitle>{{ t('subtitle') }}</template>
    <template #content>
      <form class="auth-form mt-4 flex flex-col gap-4" @submit.prevent="onFormSubmit">
        <ul class="flex flex-col gap-3">
          <li v-for="key in benefits" :key="key" class="flex items-center gap-3">
            <i class="pi pi-check-circle bg-success-50 text-success-800 mt-0.5 shrink-0 rounded-full p-1.5 text-xs" />
            <i18n-t :keypath="`benefits.${key}.text`" tag="span" class="text-surface-700 text-sm">
              <template #bold>
                <strong class="text-surface-900 font-semibold">{{ t(`benefits.${key}.bold`) }}</strong>
              </template>
            </i18n-t>
          </li>
        </ul>
        <div class="mt-4 flex flex-col">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-0.5">
              <IconField>
                <InputIcon class="pi pi-user" />
                <InputText
                  v-model="form.first_name"
                  name="first_name"
                  :placeholder="t('form.first_name.placeholder')"
                  :invalid="!!form.errors.first_name"
                  required
                  fluid />
              </IconField>
              <div class="h-5">
                <Message
                  v-if="form.errors.first_name"
                  severity="error"
                  size="small"
                  variant="simple"
                  class="auth-form-error-message">
                  {{ form.errors.first_name }}
                </Message>
              </div>
            </div>
            <div class="flex flex-col gap-0.5">
              <IconField>
                <InputIcon class="pi pi-user" />
                <InputText
                  v-model="form.last_name"
                  name="last_name"
                  :placeholder="t('form.last_name.placeholder')"
                  :invalid="!!form.errors.last_name"
                  required
                  fluid />
              </IconField>
              <div class="h-5">
                <Message
                  v-if="form.errors.last_name"
                  severity="error"
                  size="small"
                  variant="simple"
                  class="auth-form-error-message">
                  {{ form.errors.last_name }}
                </Message>
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-0.5">
            <IconField>
              <InputIcon class="pi pi-envelope" />
              <InputText
                v-model="form.email"
                name="email"
                type="email"
                :placeholder="t('form.email.placeholder')"
                :invalid="!!form.errors.email"
                required
                fluid />
            </IconField>
            <div class="h-5">
              <Message
                v-if="form.errors.email"
                severity="error"
                size="small"
                variant="simple"
                class="auth-form-error-message">
                {{ form.errors.email }}
              </Message>
            </div>
          </div>
          <div class="flex flex-col gap-0.5">
            <IconField>
              <InputIcon class="pi pi-lock" />
              <Password
                v-model="form.password"
                name="password"
                :placeholder="t('form.password.placeholder')"
                toggle-mask
                :invalid="!!form.errors.password"
                :feedback="false"
                required
                fluid />
            </IconField>
            <div class="h-5">
              <Message
                v-if="form.errors.password"
                severity="error"
                size="small"
                variant="simple"
                class="auth-form-error-message">
                {{ form.errors.password }}
              </Message>
            </div>
          </div>
          <div class="flex flex-col gap-4 pb-4">
            <label for="property_count" class="text-sm">
              <strong class="text-surface-900 font-semibold">{{ t('form.property_count.label_lead') }}</strong>
              <span class="text-surface-500">{{ t('form.property_count.label_question') }}</span>
            </label>
            <div class="flex flex-col gap-2">
              <SelectButton
                v-model="form.property_count"
                input-id="property_count"
                :options="propertyCountOptions"
                option-label="label"
                option-value="value"
                :invalid="!!form.errors.property_count"
                class="w-full" />
            </div>
            <div v-if="form.errors.property_count" class="h-5">
              <Message severity="error" size="small" variant="simple" class="auth-form-error-message">
                {{ form.errors.property_count }}
              </Message>
            </div>
          </div>
          <div class="mt-4 flex flex-col">
            <div :class="form.errors.accept_legal ? 'pb-4' : 'pb-2'" class="relative flex flex-col gap-0.5">
              <label class="flex items-center gap-4 text-sm">
                <Checkbox
                  v-model="form.accept_legal"
                  name="accept_legal"
                  binary
                  :invalid="!!form.errors.accept_legal" />
                <i18n-t keypath="form.accept_legal.label" tag="span">
                  <template #terms>
                    <a
                      :href="page.props.legalUrls.terms"
                      class="text-surface-900 font-medium"
                      target="_blank"
                      rel="noopener noreferrer">
                      {{ t('form.accept_legal.terms') }}
                    </a>
                  </template>
                  <template #privacy>
                    <a
                      :href="page.props.legalUrls.privacy"
                      class="text-surface-900 font-medium"
                      target="_blank"
                      rel="noopener noreferrer">
                      {{ t('form.accept_legal.privacy') }}
                    </a>
                  </template>
                </i18n-t>
              </label>
              <div v-if="form.errors.accept_legal" class="pt-1">
                <Message
                  v-if="form.errors.accept_legal"
                  severity="error"
                  size="small"
                  variant="simple"
                  class="auth-form-error-message">
                  {{ form.errors.accept_legal }}
                </Message>
              </div>
            </div>
          </div>
          <div class="mt-10 flex flex-col items-end gap-3">
            <Button
              type="submit"
              :label="t('form.submit')"
              icon="pi pi-arrow-right ml-2"
              icon-pos="right"
              raised
              :dt="{ padding: { x: '4rem' } }"
              :loading="form.processing" />
            <div class="text-surface-500 flex items-center gap-2 text-xs">
              <i class="pi pi-lock text-xs" />
              <span>{{ t('form.trust') }}</span>
            </div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <Divider />
      <div class="flex items-center justify-between">
        <div>{{ t('links.question') }}</div>
        <Button v-slot="slotProps" variant="link" as-child>
          <Link :href="login()" v-bind="slotProps">{{ t('links.login') }}</Link>
        </Button>
      </div>
    </template>
  </AuthCard>
</template>

<i18n>
{
    "en": {
        "eyebrow": "Almost there",
        "title": "Create your free account",
        "subtitle": "Your free cockpit for real estate & financing – ready to go in under 60 seconds.",
        "benefits": {
            "portfolio": {
                "text": "{bold} – all your properties at a glance in one place",
                "bold": "Manage your portfolio for free"
            },
            "financing": {
                "text": "{bold} – with no obligation",
                "bold": "Start a financing request whenever you're ready"
            },
            "matching": {
                "text": "Then we {bold} – instead of unnecessary calls",
                "bold": "match you with the right bank"
            }
        },
        "form": {
            "first_name": {
                "placeholder": "First name",
                "required": "First name is required"
            },
            "last_name": {
                "placeholder": "Last name",
                "required": "Last name is required"
            },
            "email": {
                "placeholder": "Email",
                "required": "Email is required",
                "email": "Must be a valid email address"
            },
            "password": {
                "placeholder": "Password",
                "length": "Password must be at least 8 characters long"
            },
            "property_count": {
                "label_lead": "So we can see whether the platform is right for you: ",
                "label_question": "How many properties do you already own?",
                "required": "Please select how many properties you have.",
                "options": {
                    "none": "0",
                    "one_to_two": "1-2",
                    "three_to_nine": "3-9",
                    "ten_to_twenty_nine": "10-29",
                    "thirty_plus": "30 or more"
                }
            },
            "accept_legal": {
                "label": "I accept the {terms} and acknowledge the {privacy}.",
                "terms": "Terms of Service",
                "privacy": "Privacy Policy",
                "required": "You must accept the terms and acknowledge the privacy policy"
            },
            "submit": "Secure free access",
            "trust": "GDPR-compliant storage in Germany · no hidden costs"
        },
        "links": {
            "question": "Already have an account?",
            "login": "Login"
        },
        "error": {
            "summary": "Registration failed",
            "detail": "Your account could not be created right now. Please try again."
        }
    },
    "de": {
        "eyebrow": "Fast geschafft",
        "title": "Erstelle dein kostenloses Konto",
        "subtitle": "Dein kostenloses Cockpit für Immobilien & Finanzierung – in unter 60 Sekunden startklar.",
        "benefits": {
            "portfolio": {
                "text": "{bold} – all deine Immobilien an einem Ort im Blick",
                "bold": "Portfolio kostenlos verwalten"
            },
            "financing": {
                "text": "{bold} – ganz ohne Verpflichtung",
                "bold": "Finanzierungsanfrage starten, sobald du bereit bist"
            },
            "matching": {
                "text": "Dann {bold} – statt unnötiger Gespräche",
                "bold": "matchen wir dich mit der passenden Bank"
            }
        },
        "form": {
            "first_name": {
                "placeholder": "Vorname",
                "required": "Vorname ist erforderlich"
            },
            "last_name": {
                "placeholder": "Nachname",
                "required": "Nachname ist erforderlich"
            },
            "email": {
                "placeholder": "E-Mail",
                "required": "E-Mail ist erforderlich",
                "email": "Muss eine gültige E-Mail-Adresse sein"
            },
            "password": {
                "placeholder": "Passwort",
                "length": "Das Passwort muss mindestens 8 Zeichen lang sein"
            },
            "property_count": {
                "label_lead": "Damit wir sehen, ob die Plattform zu dir passt: ",
                "label_question": "Wie viele Immobilien besitzt du bereits?",
                "required": "Bitte wähle aus, wie viele Immobilien du hast.",
                "options": {
                    "none": "0",
                    "one_to_two": "1-2",
                    "three_to_nine": "3-9",
                    "ten_to_twenty_nine": "10-29",
                    "thirty_plus": "30 oder mehr"
                }
            },
            "accept_legal": {
                "label": "Ich akzeptiere die {terms} und habe die {privacy} zur Kenntnis genommen.",
                "terms": "AGB",
                "privacy": "Datenschutzerklärung",
                "required": "Du musst die AGB akzeptieren und die Datenschutzerklärung zur Kenntnis nehmen."
            },
            "submit": "Kostenlos Zugang sichern",
            "trust": "DSGVO-konform in Deutschland gespeichert · keine versteckten Kosten"
        },
        "links": {
            "question": "Hast du bereits ein Konto?",
            "login": "Anmelden"
        },
        "error": {
            "summary": "Registrierung fehlgeschlagen",
            "detail": "Dein Konto konnte gerade nicht erstellt werden. Bitte versuche es erneut."
        }
    }
}
</i18n>
